<div>
  <div class="products-header">
    <div>
      <h1>Products {{ team ? 'for Team ' + team.name : '' }}</h1>
      <div class="summary">{{ selectedProductCount }} Active Products</div>
    </div>

    <div class="actions">
      <button
        mat-button
        type="button"
        [disabled]="isLoading || isSaving || !hasProductSelectionChanges()"
        (click)="cancelProductChanges()"
        >
        Cancel
      </button>
      @if (teamId) {
        <button
          mat-button
          type="button"
          [disabled]="isLoading || isSaving || !hasTeamProductOverride()"
          (click)="clearTeamProductOverride()"
          >
          <span class="button-content">
            @if (isResetting) {
              <mat-spinner [diameter]="16"></mat-spinner>
            }
            <span>{{ isResetting ? 'Clearing...' : 'Clear Team Override' }}</span>
          </span>
        </button>
      }
      <button
        mat-raised-button
        color="primary"
        type="button"
        [disabled]="isLoading || isSaving || !hasProductSelectionChanges()"
        (click)="saveProducts()"
        >
        <span class="button-content">
          @if (isSaving && !isResetting) {
            <mat-spinner [diameter]="16"></mat-spinner>
          }
          <span>{{ isSaving && !isResetting ? 'Saving...' : 'Save' }}</span>
        </span>
      </button>
    </div>
  </div>

  @if (saveError) {
    <div class="error">{{ saveError }}</div>
  }

  @if (busyStatusMessage) {
    <div class="busy-status" role="status" aria-live="polite">
      <mat-progress-bar mode="indeterminate"></mat-progress-bar>
      <div>{{ busyStatusMessage }}</div>
    </div>
  }

  @if (teamId && (sourceTeamOptions.length > 0 || isSourceTeamOptionsLoading || sourceTeamOptionsError)) {
    <div class="source-team-helper">
      <button
        type="button"
        class="source-team-helper-header"
        [attr.aria-expanded]="isSourceTeamProductHelperExpanded"
        (click)="toggleSourceTeamProductHelper()"
        >
        <span class="source-team-helper-title">Select products from existing team</span>
        <span class="source-team-helper-action">{{ isSourceTeamProductHelperExpanded ? 'Hide' : 'Show' }}</span>
      </button>
      @if (isSourceTeamProductHelperExpanded) {
        <div class="source-team-helper-body">
          <p class="source-team-helper-message">
            Copy products from another team into this selection. This does not link the teams; the source team is not saved.
          </p>
          <div class="source-team-helper-controls">
            <mat-form-field appearance="outline">
              <mat-label>Team product source</mat-label>
              <mat-select
                [(ngModel)]="selectedSourceTeamId"
                (selectionChange)="onSourceTeamChange()"
                placeholder="Choose a team"
                [disabled]="isSourceTeamOptionsLoading || isSourceTeamProductPreviewLoading || sourceTeamOptions.length === 0"
                >
                @for (sourceTeam of sourceTeamOptions; track sourceTeam) {
                  <mat-option [value]="sourceTeam.id">
                    {{ sourceTeam.label }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
            <button
              mat-stroked-button
              type="button"
              color="primary"
              [disabled]="!selectedSourceTeamId || isSourceTeamProductPreviewLoading || isSourceTeamOptionsLoading"
              (click)="loadSourceTeamProductPreview()"
              >
              Preview Team Products
            </button>
            @if (isSourceTeamOptionsLoading || isSourceTeamProductPreviewLoading) {
              <mat-spinner [diameter]="24"></mat-spinner>
            }
          </div>
          @if (sourceTeamOptionsError) {
            <div class="source-team-helper-message warning">{{ sourceTeamOptionsError }}</div>
          }
          @if (sourceTeamProductPreviewError) {
            <div class="source-team-helper-message warning">{{ sourceTeamProductPreviewError }}</div>
          }
          @if (sourceTeamProductPreviewProductIds.length > 0 || sourceTeamProductPreviewSkippedProductIds.length > 0) {
            <div class="source-team-preview">
              @if (sourceTeamProductPreviewProductIds.length === 0 && sourceTeamProductPreviewSkippedProductIds.length > 0) {
                <div
                  class="source-team-helper-message warning"
                  >
                  No source products are available for this team. The current selection was not changed.
                </div>
              }
              <p class="source-team-summary">
                {{ sourceTeamProductPreviewProductIds.length }} matched products from the selected team.
                {{ sourceTeamProductPreviewAddedProductIds.length }} would be added,
                {{ sourceTeamProductPreviewOverlappingProductIds.length }} are already selected, and
                {{ sourceTeamProductPreviewRemovedProductIds.length }} would be removed.
                {{ sourceTeamProductPreviewAllowanceEligibleProductIds.length }} copied products allow payment with Wallit allowance.
              </p>
              <div class="source-team-diff">
                <div>
                  <h3>Would Add</h3>
                  @if (sourceTeamProductPreviewAddedProductIds.length === 0) {
                    <p>No products</p>
                  }
                  @if (sourceTeamProductPreviewAddedProductIds.length > 0) {
                    <ul>
                      @for (productId of sourceTeamProductPreviewAddedProductIds; track productId) {
                        <li>{{ getProductDisplay(productId) }}</li>
                      }
                    </ul>
                  }
                </div>
                <div>
                  <h3>Already Selected</h3>
                  @if (sourceTeamProductPreviewOverlappingProductIds.length === 0) {
                    <p>No products</p>
                  }
                  @if (sourceTeamProductPreviewOverlappingProductIds.length > 0) {
                    <ul>
                      @for (productId of sourceTeamProductPreviewOverlappingProductIds; track productId) {
                        <li>{{ getProductDisplay(productId) }}</li>
                      }
                    </ul>
                  }
                </div>
                <div>
                  <h3>Would Remove</h3>
                  @if (sourceTeamProductPreviewRemovedProductIds.length === 0) {
                    <p>No products</p>
                  }
                  @if (sourceTeamProductPreviewRemovedProductIds.length > 0) {
                    <ul>
                      @for (productId of sourceTeamProductPreviewRemovedProductIds; track productId) {
                        <li>{{ getProductDisplay(productId) }}</li>
                      }
                    </ul>
                  }
                </div>
                @if (sourceTeamProductPreviewSkippedProductIds.length > 0) {
                  <div>
                    <h3>Skipped</h3>
                    <ul>
                      @for (productId of sourceTeamProductPreviewSkippedProductIds; track productId) {
                        <li>{{ productId }}</li>
                      }
                    </ul>
                  </div>
                }
              </div>
              <button
                mat-raised-button
                type="button"
                color="accent"
                [disabled]="!canApplySourceTeamProductPreview()"
                (click)="applySourceTeamProductPreview()"
                >
                Apply To Selection
              </button>
              @if (sourceTeamProductPreviewApplied) {
                <p class="source-team-applied">
                  Team products were applied to the current selection. Use Save to persist.
                </p>
              }
            </div>
          }
        </div>
      }
    </div>
  }

  <div class="section-shell">
    <div
      #productsSection
      class="section"
      [attr.aria-busy]="isLoading || isSaving"
      (scroll)="updateProductScrollState()"
      >
      @if (isLoading) {
        <div class="placeholder">
          <span>Loading products...</span>
        </div>
      }

      @if (!isLoading && products.length > 0) {
        <div #productList class="product-list" [class.product-list-busy]="isSaving">
          @if (teamId) {
            <div class="product-context">
              @if (!hasTeamProductOverride()) {
                <div class="override-status">Using company-level product settings</div>
              }
              <div class="helper-text">
                This list edits team-specific product overrides. Company-level products are set by Wallit; please reach out to request updates.
              </div>
            </div>
          }
          <div class="bulk-controls">
            <mat-checkbox
              class="bulk-product-control"
              aria-label="Select all products"
              [checked]="areAllSelectableProductsSelected()"
              [indeterminate]="areSomeSelectableProductsSelected()"
              [disabled]="isProductBulkSelectionDisabled"
              (change)="toggleAllProductSelections($event.checked)"
            ></mat-checkbox>
            @if (teamId) {
              <mat-checkbox
                class="bulk-allowance-control"
                aria-label="Allow payment with Wallit allowance for all selected products"
                [checked]="areAllAllowanceTargetsEligible()"
                [indeterminate]="areSomeAllowanceTargetsEligible()"
                [disabled]="isAllowanceBulkSelectionDisabled"
                (change)="toggleAllAllowanceEligibility($event.checked)"
                >
                <span class="bulk-label-spacer" aria-hidden="true">Allows payment with Wallit allowance</span>
              </mat-checkbox>
            }
          </div>
          @for (item of products; track trackByProductId($index, item)) {
            <div class="item">
              <div class="product-row">
                <mat-checkbox
                  [checked]="item.selected"
                  [disabled]="item.locked || isSaving"
                  (change)="toggleProductSelection(item, $event.checked)"
                ></mat-checkbox>
                <div class="logo-slot">
                  @if (item.loadedImage) {
                    <img class="logo" [src]="item.loadedImage" alt="" />
                  }
                </div>
                <div class="itemright">
                  <span class="header">{{ item.vendorName }}</span>
                  <span class="description">{{ item.productName }}</span>
                </div>
                @if (canManageAllowanceEligibility(item)) {
                  <div class="allowance-control">
                    <mat-checkbox
                      class="allowance-checkbox"
                      [checked]="item.allowanceEligible"
                      [disabled]="!canToggleAllowanceEligibility(item) || item.allowanceSaving || isSaving"
                      (change)="toggleAllowanceEligibility(item, $event.checked)"
                      >
                      Allows payment with Wallit allowance
                    </mat-checkbox>
                    @if (item.allowanceSaving) {
                      <mat-spinner [diameter]="18"></mat-spinner>
                    }
                  </div>
                }
                @if (item.locked) {
                  <span class="locked-label">Always Active</span>
                }
              </div>
            </div>
          }
        </div>
      }

      @if (!isLoading && products.length === 0) {
        <div class="placeholder">
          No products available.
        </div>
      }
    </div>
    @if (showProductScrollHint) {
      <div class="product-scroll-hint" aria-hidden="true">
        <span>Scroll for more products</span>
        <mat-icon>keyboard_arrow_down</mat-icon>
      </div>
    }
  </div>
</div>
